'use client';

import clsx from 'clsx';
import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import { EmblaOptionsType } from 'embla-carousel';
import { PropsWithChildren, useEffect, useEffectEvent, useState } from 'react';

type Props = {
  showPrevNextButtons?: boolean;
  showDots?: boolean;
  className?: string;
  config?: EmblaOptionsType;
};

const defaultConfig = {
  draggable: true,
  breakpoints: {
    '(min-width: 1040px)': { draggable: false },
  },
} satisfies EmblaOptionsType;

function EmblaCarousel(props: PropsWithChildren<Props>) {
  const { children, showPrevNextButtons, showDots, className, config } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel(config ?? defaultConfig);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [selectedSnap, setSelectedSnap] = useState(0);

  const goToPrev = () => emblaApi?.goToPrev();
  const goToNext = () => emblaApi?.goToNext();

  function goTo(index: number) {
    emblaApi?.goTo(index);
  }

  const setupSnaps = useEffectEvent((emblaApi: UseEmblaCarouselType[1]) => {
    if (!emblaApi) return void 0;
    setScrollSnaps(emblaApi.snapList());
  });
  const setActiveSnap = useEffectEvent((emblaApi: UseEmblaCarouselType[1]) => {
    if (!emblaApi) return void 0;
    setSelectedSnap(emblaApi.selectedSnap());
  });

  useEffect(() => {
    if (!emblaApi || !showDots) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setActiveSnap(emblaApi);

    emblaApi.on('reinit', setupSnaps);
    emblaApi.on('reinit', setActiveSnap);
    emblaApi.on('select', setActiveSnap);

    setupSnaps(emblaApi);
  }, [emblaApi, showDots]);

  return (
    <div className={clsx('embla', className)} role='region' aria-roledescription='carousel'>
      <div className='embla__viewport' ref={emblaRef}>
        <div className='embla__container'>{children}</div>
      </div>

      {showPrevNextButtons ? (
        <>
          <button className='embla__prev' onClick={goToPrev}>
            <span className='sr-only'>Scroll to prev slide</span>
          </button>
          <button className='embla__next' onClick={goToNext}>
            <span className='sr-only'>Scroll to next slide</span>
          </button>
        </>
      ) : null}
      {showDots && scrollSnaps?.length > 1 ? (
        <div className='embla__dots'>
          {scrollSnaps.map((_, index) => (
            <button
              className={clsx('embla__dot', {
                'embla__dot--selected': selectedSnap === index,
              })}
              key={index}
              onClick={() => goTo(index)}
            >
              <span className='sr-only'>Scroll to slide {index + 1}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default EmblaCarousel;
